import { Menu as BaseMenu } from '@base-ui/react/menu'
import { type ReactElement, useState } from 'react'
import { Link } from 'react-router-dom'
import { cx } from '../cx'
import { Icon } from '../Icon/Icon'
import type { IconName } from '../Icon/icons'
import { Kbd } from '../Kbd/Kbd'
import { useOverlayLayer } from '../overlay/escapeStack'
import { overlayClass } from '../overlay/Overlay'
import {
  type Placement,
  spaceOffset,
  toSideAlign,
} from '../overlay/positioning'
import styles from './Menu.module.css'

// Wraps Base UI Menu. Deviations: highlightItemOnHover off, so hover (raise)
// and keyboard highlight (selected) differ (Menu card, component-states);
// Escape via the page-wide stack. Tested. Icons are all or none; a destructive
// item is last, after a separator (Menu card; the caller orders items).

type MenuAction = {
  id: string
  label: string
  /** The leading visual: an icon name, or an `sm` Avatar (the same 16px box). */
  icon?: IconName | ReactElement
  shortcut?: readonly string[]
  tone?: 'danger'
  disabled?: boolean
  onSelect: () => void
}

type MenuLink = {
  id: string
  label: string
  /** The leading visual: an icon name, or an `sm` Avatar (the same 16px box). */
  icon?: IconName | ReactElement
  href: string
}

type MenuItem = MenuAction | MenuLink

export type MenuEntry =
  | MenuItem
  | { type: 'separator'; id: string }
  | { type: 'group'; id: string; label: string; items: readonly MenuItem[] }

function isLink(item: MenuItem): item is MenuLink {
  return 'href' in item
}

function renderItem(item: MenuItem) {
  const icon =
    typeof item.icon === 'string' ? (
      <Icon name={item.icon} size="icon-embedded" />
    ) : (
      item.icon
    )
  if (isLink(item)) {
    return (
      <BaseMenu.LinkItem
        key={item.id}
        label={item.label}
        closeOnClick
        render={<Link to={item.href} />}
        className={styles.item}
      >
        {icon}
        <span className={styles.label}>{item.label}</span>
      </BaseMenu.LinkItem>
    )
  }
  return (
    <BaseMenu.Item
      key={item.id}
      label={item.label}
      disabled={item.disabled}
      onClick={item.onSelect}
      className={styles.item}
      data-tone={item.tone}
    >
      {icon}
      <span className={styles.label}>{item.label}</span>
      {item.shortcut && (
        <Kbd keys={item.shortcut} className={styles.shortcut} />
      )}
    </BaseMenu.Item>
  )
}

function renderEntry(entry: MenuEntry) {
  if ('type' in entry) {
    if (entry.type === 'separator') {
      return <BaseMenu.Separator key={entry.id} className={styles.separator} />
    }
    return (
      <BaseMenu.Group key={entry.id}>
        <BaseMenu.GroupLabel className={styles.groupLabel}>
          {entry.label}
        </BaseMenu.GroupLabel>
        {entry.items.map(renderItem)}
      </BaseMenu.Group>
    )
  }
  return renderItem(entry)
}

export function Menu({
  trigger,
  items,
  placement = 'bottom-start',
  open,
  defaultOpen,
  onOpenChange,
}: {
  trigger: ReactElement
  items: readonly MenuEntry[]
  placement?: Placement
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const [inner, setInner] = useState(defaultOpen ?? false)
  const isOpen = open ?? inner
  const handle = useOverlayLayer(isOpen, (next) => {
    setInner(next)
    onOpenChange?.(next)
  })
  const { side, align } = toSideAlign(placement)

  return (
    <BaseMenu.Root
      open={isOpen}
      onOpenChange={handle}
      highlightItemOnHover={false}
    >
      <BaseMenu.Trigger render={trigger} />
      <BaseMenu.Portal>
        <BaseMenu.Positioner
          side={side}
          align={align}
          sideOffset={spaceOffset('space-4')}
          className={styles.positioner}
        >
          <BaseMenu.Popup
            className={cx(overlayClass.surface, styles.popup)}
            data-weight="sm"
          >
            {items.map(renderEntry)}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  )
}
